import type { operations, components } from '@shopware/api-client/api-types/storeApiTypes';
import type { PdpDataResponse } from './pdpData';
import { getCookie, setCookie, removeCookie } from '../utils/cookieStorage';

const CONTEXT_TOKEN_KEY = 'sw-context-token';

/**
 * Product fields configuration
 * Defines which fields to fetch on a product
 */
const PRODUCT_INCLUDES = {
  // categoryIds/manufacturerId/tagIds/streamIds are needed for promo eligibility
  // matching (see usePromoCode.computeEligibility); the Store API strips any field
  // not whitelisted here, so without them scope-based promos never match.
  product: ['id', 'active', 'productNumber', 'available', 'availableStock', 'calculatedPrice', 'calculatedPrices', 'unit', 'deliveryTime', 'customFields', 'categoryIds', 'manufacturerId', 'tagIds', 'streamIds'],
  calculated_price: ['unitPrice', 'referencePrice', 'listPrice'],
  unit: ['translated', 'shortCode'],
  property_group_option_translated: ['name', 'shortCode'],
  // delivery_time: ['name'], // Commented out to get full deliveryTime object, not just name field
} as const;

/* use this to fetch all product fields */
// const PRODUCT_INCLUDES = undefined;

/**
 * Get the current Shopware context token from cookie
 */
export function getContextToken(): string {
  return getCookie(CONTEXT_TOKEN_KEY) || '';
}

// ORT-3109: the sw-context-token that a customer login established. login() (POST /account/login)
// makes Shopware REGENERATE the context token, so calling it on every PDP rotates the token and
// drops the cart. We remember the established token so PDPs can reuse it instead of re-logging-in.
//
// Stored in localStorage, NOT sessionStorage: the sw-context-token cookie is shared across tabs, so
// the marker must be too. With a per-tab marker, opening a PDP in a second tab (or the first PDP of
// a new tab) would find no marker, call login() again, rotate the shared token, and orphan the cart
// held by the other tab. localStorage keeps the marker in lockstep with the shared cookie. Cleared
// on logout; a token mismatch (cookie changed) triggers a fresh login, so staleness self-heals.
const CUSTOMER_ESTABLISHED_KEY = 'orthomol_sw_customer_token';
export function getEstablishedCustomerToken(): string | null {
  try {
    return window.localStorage.getItem(CUSTOMER_ESTABLISHED_KEY);
  } catch {
    return null;
  }
}
export function rememberEstablishedCustomerToken(token: string): void {
  try {
    window.localStorage.setItem(CUSTOMER_ESTABLISHED_KEY, token);
  } catch {
    /* localStorage unavailable, best-effort */
  }
}
export function clearEstablishedCustomerToken(): void {
  try {
    window.localStorage.removeItem(CUSTOMER_ESTABLISHED_KEY);
  } catch {
    /* ignore */
  }
}

// Configuration

// Declare window.SHOPWARE_CONFIG type
declare global {
  interface Window {
    SHOPWARE_CONFIG?: {
      baseURL: string;
      accessToken: string;
    };
  }
}

// Get configuration from window.SHOPWARE_CONFIG (set via environment variables in _head.html.twig)
function getShopwareConfig(): { baseURL: string; accessToken: string } {
  if (typeof window === 'undefined' || !window.SHOPWARE_CONFIG?.baseURL || !window.SHOPWARE_CONFIG?.accessToken) {
    throw new Error(
      'SHOPWARE_CONFIG is not available. Make sure SHOPWARE_BASE_URL and SHOPWARE_STORE_API_ACCESS_KEY environment variables are set.'
    );
  }
  return window.SHOPWARE_CONFIG;
}

const SHOPWARE_CONFIG = getShopwareConfig();

// Extract types from operations
export type Product = components['schemas']['Product'];
export type ProductListingResult = components['schemas']['ProductListingResult'];
export type Cart = components['schemas']['Cart'];
export type LineItem = components['schemas']['LineItem'];
export type OrderLineItem = components['schemas']['OrderLineItem'];
export type PaymentMethod = components['schemas']['PaymentMethod'];
export type Order = components['schemas']['Order'];
export type Document = components['schemas']['Document'];
export type Customer = components['schemas']['Customer'];

// Helper type to extract request body type
type RequestBody<T> = T extends { body: infer B } ? B : never;

// Helper type to extract response type
type ResponseData<T> = T extends { response: infer R } ? R : never;

/** One entry of the Store API error envelope `{ errors: [...] }` */
export interface ShopwareErrorEntry {
  status?: string;
  code?: string;
  title?: string;
  detail?: string;
  meta?: unknown;
}

/**
 * Thrown by shopwareFetch for non-2xx responses. Carries the HTTP status and the parsed error envelope,
 * so callers can react to a status or error code instead of parsing the message. The message keeps
 * its previous format (`Shopware API Error: <status> ...`), existing `.message.includes('403')` checks still work.
 */
export class ShopwareApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly errors: ShopwareErrorEntry[],
    message: string
  ) {
    super(message);
    this.name = 'ShopwareApiError';
  }
}

interface ShopwareFetchOptions extends RequestInit {
  /**
   * If true, will attempt to re-login and retry the request on 403 errors
   * This is useful for handling expired sessions automatically
   * @default false
   */
  tryRefreshLoginOnSessionExpired?: boolean;
  /**
   * If true, the sw-context-token cookie is neither sent NOR captured from the response, keeping
   * the request fully session-independent. Used for /pdp-data so its response depends only on
   * (productNumbers, countryIso), stays edge-cacheable, and never rotates the cart's context token.
   * @default false
   */
  omitContextToken?: boolean;
}

/**
 * Generic fetch wrapper with automatic context token management
 * - Reads sw-context-token from cookie and sends it with every request
 * - Captures token from response (body or headers) and updates cookie
 * - This ensures we always reuse the same Shopware session
 * - Optionally retries on 403 errors after attempting to re-login
 *
 * @param endpoint - API endpoint path (e.g., '/account/login')
 * @param options - Standard fetch options with optional retry on session expiry
 */
async function shopwareFetch<T>(endpoint: string, options: ShopwareFetchOptions = {}): Promise<T> {
  const { tryRefreshLoginOnSessionExpired, omitContextToken, ...fetchOptions } = options;

  // Read current context token from cookie (skipped for session-independent calls)
  const contextToken = omitContextToken ? '' : getCookie(CONTEXT_TOKEN_KEY);

  // Use relative path for login/logout so Apache can proxy it for SSO token handling
  // This is necessary because Shopware runs on a different domain (e.g., stage-sw6.orthomol.com)
  // and httpOnly SSO cookies can't be sent in cross-origin requests

  // TODO: Remove this once the cookie can be sent without proxy
  const isLoginOrLogout = endpoint === '/account/login' || endpoint === '/account/logout';
  const useRelativePath = isLoginOrLogout;

  const url = useRelativePath ? `/store-api${endpoint}` : `${SHOPWARE_CONFIG.baseURL}/store-api${endpoint}`;

  const response = await fetch(url, {
    ...fetchOptions,
    credentials: fetchOptions.credentials || 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'sw-access-key': SHOPWARE_CONFIG.accessToken,
      ...(contextToken ? { 'sw-context-token': contextToken } : {}),
      ...fetchOptions.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new ShopwareApiError(
      response.status,
      Array.isArray(errorData?.errors) ? errorData.errors : [],
      `Shopware API Error: ${response.status} ${response.statusText}${Object.keys(errorData).length ? ` - ${JSON.stringify(errorData)}` : ''}`
    );

    // Handle 403 errors with automatic re-login and retry
    if (response.status === 403 && tryRefreshLoginOnSessionExpired && !isLoginOrLogout) {
      console.log('[shopwareFetch] 403 error detected, attempting to re-login and retry...');
      try {
        // Attempt to re-login (without retry flag to prevent infinite loops)
        await shopwareFetch('/account/login', {
          method: 'POST',
          credentials: 'include',
          tryRefreshLoginOnSessionExpired: false,
        });

        console.log('[shopwareFetch] Re-login successful, retrying original request...');

        // Retry the original request (without retry flag to prevent infinite loops)
        return await shopwareFetch<T>(endpoint, {
          ...options,
          tryRefreshLoginOnSessionExpired: false,
        });
      } catch (retryError) {
        console.error('[shopwareFetch] Retry failed after re-login:', retryError);
        throw retryError;
      }
    }

    throw error;
  }

  const data = await response.json();

  // Capture and save context token from response (either body or headers)
  // Priority: 1) body.token (cart responses), 2) body.contextToken (login), 3) response header
  //
  // Session-independent calls (omitContextToken, e.g. anonymous /pdp-data) must NOT persist the
  // returned token: they are sent without a token, so Shopware mints a FRESH anonymous context and
  // returns it. Saving that would overwrite the cart's real sw-context-token, orphaning the cart on
  // every PDP load (product A's cart lost when product B's /pdp-data rotates the token). Such calls
  // stay fully stateless: neither send nor capture the token.
  if (!omitContextToken) {
    const contextTokenFromBody = (data as any)?.token || (data as any)?.contextToken;
    const contextTokenFromHeader = response.headers.get('sw-context-token');
    const newToken = contextTokenFromBody || contextTokenFromHeader;

    if (newToken && newToken !== contextToken) {
      setCookie(CONTEXT_TOKEN_KEY, newToken);
    }
  }

  return data;
}

/**
 * Fetch navigation/categories
 */
export async function fetchCategories(
  depth: number = 2
): Promise<ResponseData<operations['readNavigation post /navigation/{activeId}/{rootId}']>> {
  return shopwareFetch('/navigation/main-navigation/main-navigation', {
    method: 'POST',
    body: JSON.stringify({ depth }),
  });
}

/**
 * Fetch product listing by category
 */
export async function fetchProductListing(
  categoryId: string,
  body: RequestBody<operations['readProductListing post /product-listing/{categoryId}']>,
  options?: { includeAllFields?: boolean }
): Promise<ResponseData<operations['readProductListing post /product-listing/{categoryId}']>> {
  // Apply product includes configuration (unless includeAllFields is true)
  const requestBody = {
    ...body,
    includes: options?.includeAllFields ? undefined : PRODUCT_INCLUDES,
  };

  return shopwareFetch(`/product-listing/${categoryId}?skipCmsPage=true&skipConfigurator=true`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
}

/**
 * Fetch single product details
 */
export async function fetchProductDetail(
  productId: string,
  options?: { includeAllFields?: boolean }
): Promise<ResponseData<operations['readProductDetail post /product/{productId}']>> {
  // Apply product includes configuration (unless includeAllFields is true)
  const requestBody = {
    includes: options?.includeAllFields ? undefined : PRODUCT_INCLUDES,
  };

  return shopwareFetch(`/product/${productId}?skipCmsPage=true&skipConfigurator=true`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
}

/**
 * Fetch consolidated PDP data (product + base/rule prices + promotions + shipping) for
 * one or more product numbers from valantic's custom /pdp-data endpoint.
 *
 * Anonymous requests are sent WITHOUT the sw-context-token (session-independent, so the response
 * depends only on (productNumbers, countryIso) and stays edge-cacheable). For a logged-in
 * customer (ORT-3109) pass `withCustomerToken=true`: the customer context token is then sent and
 * customer-specific rules apply. Such responses are personalized and must not be shared/edge-cached.
 * The typed adapter lives in pdpData.ts.
 */
export async function fetchPdpDataRaw(
  productNumbers: string[],
  countryIso?: string,
  withCustomerToken = false
): Promise<PdpDataResponse> {
  const body: { productNumbers: string[]; countryIso?: string } = { productNumbers };
  if (countryIso) body.countryIso = countryIso;

  return shopwareFetch<PdpDataResponse>('/pdp-data', {
    method: 'POST',
    body: JSON.stringify(body),
    omitContextToken: !withCustomerToken,
  });
}

/**
 * Get current cart
 */
export async function fetchCart(): Promise<ResponseData<operations['readCart get /checkout/cart']>> {
  return shopwareFetch('/checkout/cart', {
    method: 'GET',
  });
}

/**
 * Add item to cart
 */
export async function addToCart(
  body: RequestBody<operations['addLineItem post /checkout/cart/line-item']>,
  // ORT-3064: pass an ISO country (e.g. AT) to switch the (guest) cart to that country.
  shippingTo?: string
): Promise<ResponseData<operations['addLineItem post /checkout/cart/line-item']>> {
  const endpoint = shippingTo
    ? `/checkout/cart/line-item?shippingTo=${encodeURIComponent(shippingTo)}`
    : '/checkout/cart/line-item';
  return shopwareFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Update cart line item
 */
export async function updateCartItem(
  body: RequestBody<operations['updateLineItem patch /checkout/cart/line-item']>,
  // ORT-3064: pass an ISO country (e.g. AT) to switch the (guest) cart to that country.
  shippingTo?: string
): Promise<ResponseData<operations['updateLineItem patch /checkout/cart/line-item']>> {
  const endpoint = shippingTo
    ? `/checkout/cart/line-item?shippingTo=${encodeURIComponent(shippingTo)}`
    : '/checkout/cart/line-item';
  return shopwareFetch(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Remove item from cart
 */
export async function removeFromCart(lineItemIds: string[]): Promise<Cart> {
  return shopwareFetch<Cart>('/checkout/cart/line-item', {
    method: 'DELETE',
    body: JSON.stringify({ ids: lineItemIds }),
  });
}

/**
 * Fetch all products (without category requirement)
 * Supports pagination, filters, sorting, etc.
 */
export async function fetchProducts(
  body?: Partial<{
    limit?: number;
    page?: number;
    ids?: string[];
    filter?: any[];
    sort?: any[];
    associations?: any;
  }>,
  options?: { includeAllFields?: boolean }
): Promise<ResponseData<operations['readProduct post /product']>> {
  // Apply product includes configuration (unless includeAllFields is true)
  const requestBody = {
    ...body,
    includes: options?.includeAllFields ? undefined : PRODUCT_INCLUDES,
  };

  return shopwareFetch('/product?skipCmsPage=true&skipConfigurator=true', {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
}

/**
 * Search products
 */
export async function searchProducts(
  query: string,
  options?: { includeAllFields?: boolean }
): Promise<ResponseData<operations['searchPage post /search']>> {
  // Apply product includes configuration (unless includeAllFields is true)
  const requestBody = {
    search: query,
    includes: options?.includeAllFields ? undefined : PRODUCT_INCLUDES,
  };

  return shopwareFetch('/search?skipCmsPage=true&skipConfigurator=true', {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
}

/**
 * Fetch multiple products by productNumbers (o-numbers) in one call.
 * Returns a record { oNumber => Product } for fields included in PRODUCT_INCLUDES.
 *
 * Single source of truth for "fetch products by numbers" — `ShopwareContext`'s
 * same-named hook method delegates here and adapts the result to a Map.
 */
export async function fetchProductsByNumbers(
  productNumbers: string[]
): Promise<Record<string, Product>> {
  if (productNumbers.length === 0) return {};

  const response = await fetchProducts({
    filter: [{ type: 'equalsAny', field: 'productNumber', value: productNumbers }],
    limit: Math.max(productNumbers.length, 50),
  });

  const map: Record<string, Product> = {};
  for (const p of response.elements ?? []) {
    if (p.productNumber) map[p.productNumber] = p;
  }
  return map;
}

/**
 * Fetch product by productNumber (o-number)
 */
export async function fetchProductByNumber(
  productNumber: string,
  options?: { includeAllFields?: boolean }
): Promise<Product | null> {
  try {
    // Build request body with product includes configuration
    const requestBody = {
      filter: [
        {
          type: 'equals',
          field: 'productNumber',
          value: productNumber,
        },
      ],
      limit: 1,
    };

    const response = await fetchProducts(requestBody, options);

    // Return the first product if found, otherwise null
    return response.elements && response.elements.length > 0 ? response.elements[0] : null;
  } catch (error) {
    console.error(`[shopwareApi] Failed to fetch product by number ${productNumber}:`, error);
    return null;
  }
}

/**
 * Fetch available payment methods
 */
export async function fetchPaymentMethods(
  onlyAvailable: boolean = true
): Promise<ResponseData<operations['readPaymentMethod post /payment-method']>> {
  return shopwareFetch('/payment-method', {
    method: 'POST',
    body: JSON.stringify({
      onlyAvailable,
    }),
  });
}

/**
 * Fetch list of orders
 * Automatically retries on 403 errors by re-logging in
 */
export async function fetchOrders(
  body?: Partial<{
    limit?: number;
    page?: number;
    filter?: any[];
    sort?: any[];
    associations?: any;
    aggregations?: any[]; // Array of aggregation objects with { name, type, field }
    'total-count-mode'?: 'none' | 'exact' | 'next-pages';
  }>
): Promise<ResponseData<operations['readOrder post /order']>> {
  const requestBody = {
    limit: 5, // Default pagination limit
    ...body,
  };

  console.log('[eshop] [shopwareApi] Fetching orders with request body:', JSON.stringify(requestBody, null, 2));

  const response = await shopwareFetch<ResponseData<operations['readOrder post /order']>>('/order', {
    method: 'POST',
    body: JSON.stringify(requestBody),
    tryRefreshLoginOnSessionExpired: true,
  });

  console.log('[eshop] [shopwareApi] Orders response:', JSON.stringify(response, null, 2));

  return response;
}

/**
 * Download a document (e.g., invoice PDF) for an order
 * Returns the document as a Blob which can be used to trigger a download
 *
 * @param documentId - The document ID from the order's documents array
 * @param deepLinkCode - The deep link code from the order's documents array
 * @param filename - Optional filename for the download (defaults to 'document.pdf')
 *
 * @example
 * const blob = await downloadDocument(documentId, deepLinkCode);
 * const url = URL.createObjectURL(blob);
 * const a = document.createElement('a');
 * a.href = url;
 * a.download = 'invoice.pdf';
 * a.click();
 */
export async function downloadDocument(documentId: string, deepLinkCode: string): Promise<Blob> {
  // Read current context token from cookie
  const contextToken = getCookie(CONTEXT_TOKEN_KEY);

  const response = await fetch(`${SHOPWARE_CONFIG.baseURL}/store-api/document/download/${documentId}/${deepLinkCode}`, {
    method: 'GET',
    headers: {
      'sw-access-key': SHOPWARE_CONFIG.accessToken,
      ...(contextToken ? { 'sw-context-token': contextToken } : {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to download document: ${response.status} ${response.statusText}`);
  }

  return await response.blob();
}

/**
 * Helper function to trigger a document download in the browser
 *
 * @param documentId - The document ID from the order's documents array
 * @param deepLinkCode - The deep link code from the order's documents array
 * @param filename - Filename for the download (defaults to 'document.pdf')
 */
export async function triggerDocumentDownload(
  documentId: string,
  deepLinkCode: string,
  filename: string = 'document.pdf'
): Promise<void> {
  const blob = await downloadDocument(documentId, deepLinkCode);
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();

  // Clean up the URL object after download
  // Delay revocation to allow browser to read the blob data asynchronously
  // This prevents download failures in Safari and with larger files
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Login to Shopware using SSO tokens from httpOnly cookie
 * The orthomol_sso_tokens cookie will be sent automatically with the request
 */
export async function login(): Promise<ResponseData<operations['loginCustomer post /account/login']>> {
  return shopwareFetch('/account/login', {
    method: 'POST',
    credentials: 'include', // Required to send orthomol_sso_tokens cookie
  });
}

/**
 * Fetch the current logged-in customer profile
 */
export async function fetchCustomer(): Promise<ResponseData<operations['readCustomer post /account/customer']>> {
  return shopwareFetch('/account/customer', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Logout from Shopware
 * Clears the server-side session and invalidates the context token
 */
export async function logout(): Promise<void> {
  await shopwareFetch<void>('/account/logout', {
    method: 'POST',
    credentials: 'include', // Required to send cookies
  });

  // Clear the context token cookie after successful logout
  removeCookie(CONTEXT_TOKEN_KEY);
  // Forget the established customer context so a later login re-establishes it (ORT-3109).
  clearEstablishedCustomerToken();
}

// ORT-3036: back-in-stock notification (valantic plugin on the Store API, no login needed).
// Shopware itself mails the subscriber once when the product is active and back in stock
// (flow val.back_in_stock.notification); there is no endpoint to send anything from here.
// Subscribing twice does not create a second entry, it re-activates an already notified one.

export interface BackInStockSubscribeResponse {
  apiAlias: 'val_back_in_stock_subscribe';
  success: boolean;
}

/**
 * Subscribe an e-mail address to the back-in-stock notification of one variant (O-Nummer).
 * Errors surface as ShopwareApiError: 400 for a missing/invalid e-mail or missing oNumber,
 * 404 when no product exists for the oNumber.
 */
export async function subscribeBackInStock(oNumber: string, email: string): Promise<BackInStockSubscribeResponse> {
  return shopwareFetch('/val-back-in-stock/subscribe', {
    method: 'POST',
    body: JSON.stringify({ oNumber, email }),
    // Session-independent: the subscription is keyed by oNumber + e-mail only. Sent without the cart's
    // context token so Shopware's reply (a freshly minted token) can never overwrite the cart cookie.
    omitContextToken: true,
  });
}
